<template>
  <section class="home-tab" :style="homeLayoutStyle">
    <header ref="homeHeaderRef" class="home-header">
      <button
        class="account-chip"
        type="button"
        @click="showAccountSheet = true"
      >
        <div class="account-chip__avatar">
          <app-image
            v-if="avatarUrl"
            class="account-chip__avatar-image"
            :src="avatarUrl"
            alt=""
          />
          <span v-else>{{ avatarText }}</span>
        </div>
        <div class="account-chip__meta">
          <div class="name">
            {{ accountName }}
            <i class="current-card-bar__caret"></i>
          </div>
          <span>{{ shortAddress(profileStore.currentAddress) || "--" }}</span>
        </div>
      </button>

      <div class="home-header__tools">
        <button
          class="node-chip"
          type="button"
          @click="router.push('/home/node')"
        >
          <span :class="['node-chip__dot', nodeStatusClass]" />
        </button>
        <button
          class="language-chip"
          type="button"
          @click="router.push('/mine/language')"
        >
          <app-image :src="languageIcon" alt="" />
        </button>
        <button
          class="network-chip"
          type="button"
          @click="router.push('/home/network')"
        >
          <app-image :src="currentNetworkIcon" alt="" />
          <app-image src="@/assets/images/homeImage/switch_arrows.png" alt="" />
        </button>
      </div>
    </header>

    <div class="home-body">
      <van-pull-refresh
        v-model="refreshing"
        class="home-refresh"
        :head-height="46"
        @refresh="handleRefresh"
      >
        <template #pulling="{ distance }">
          <CustomPullRefreshHead :distance="distance" />
        </template>
        <template #loosing="{ distance }">
          <CustomPullRefreshHead :distance="distance" />
        </template>
        <template #loading>
          <CustomPullRefreshHead loading :distance="30" />
        </template>
        <div class="home-content">
          <section v-if="showNotifyBar" class="notify-banner">
            <div class="notify-banner__content">
              <app-image class="notify-banner__icon" :src="notifyIcon" alt="" />
              <span>{{ t("home.kycRetryRequired") }}</span>
            </div>
            <button
              type="button"
              class="notify-banner__action"
              @click="openKycNotice"
            >
              {{ t("home.viewNow") }}
            </button>
            <button
              type="button"
              class="notify-banner__close"
              @click="showNotifyBar = false"
            >
              <van-icon name="cross" />
            </button>
          </section>

          <!-- <section v-if="lastTradeShow" class="trade-banner">
            <div class="trade-banner__content">
              <div data-strong>{{ t('home.bill') }}</div>
              <span>{{ t('home.cardTailNo') }} {{ lastTrade.cardNumLastFour || '--' }}</span>
              <span :class="{ fail: Number(lastTrade.status) !== 1 }">
                {{ Number(lastTrade.status) === 1 ? t('home.tradeSuccess') : t('home.tradeFailed') }}
              </span>
              <span>
                {{ Number(lastTrade.amount || 0) < 0 ? '-' : '+' }}${{ formatMoney(Math.abs(Number(lastTrade.amount || 0))) }}
              </span>
              <span>{{ formatTradeTime(lastTrade.createTime) }}</span>
            </div>
            <button type="button" @click="lastTradeShow = false">{{ t('common.close') }}</button>
          </section> -->

          <section class="asset-card">
            <AppSvgIcon
              class="asset-card__background"
              :src="assetBg"
              size="100%"
              color="var(--asset-card-background)"
            />
            <div class="asset-card__title" @click="showBalance = !showBalance">
              <span>{{ t("home.totalAssets") }}</span>
              <app-image
                :src="showBalance ? eyeOpenIcon : eyeCloseIcon"
                alt=""
              />
            </div>

            <div class="asset-card__amount">
              <div data-strong>{{ showBalance ? assetTotalText : "***" }}</div>
              <span>USDT</span>
              <!-- <button v-if="showBackupButton" type="button" @click.stop="router.push('/auth/backup-warn')">
                <van-icon name="warning" />
                {{ t('home.backup') }}
              </button> -->
            </div>

            <div class="asset-card__stats">
              <button type="button" @click="router.push('/card/index')">
                <span>{{ t("home.cardAssets") }}></span>
                <div data-strong>
                  {{ showBalance ? formatMoney(cardTotal) : "***" }}
                </div>
              </button>
              <button
                type="button"
                @click="router.push('/wallet/account-assets')"
              >
                <span>{{ t("home.accountAssets") }}></span>
                <div data-strong>
                  {{ showBalance ? formatMoney(accountTotal) : "***" }}
                </div>
              </button>
              <button
                type="button"
                @click="
                  navigateWithState(router, '/card/index', {
                    namespace: 'home-card',
                    payload: { leftArrow: true },
                  })
                "
              >
                <span>{{ t("home.cardWallet") }}></span>
                <div data-strong>{{ showBalance ? cardCount : "***" }}</div>
              </button>
            </div>
          </section>

          <section class="shortcut-row">
            <div
              class="shortcut-row-list"
              v-for="item in shortcuts"
              :key="item.label"
              @click="runShortcut(item)"
            >
              <AppSvgIcon
                v-if="item.svgIcon"
                :name="item.svgIcon"
                size="48"
                color="var(--wp-color-brand-1)"
              />
              <app-image v-else :src="item.icon" alt="" />
              <span>{{ item.label }}</span>
            </div>
          </section>

          <div class="home-divider" />

          <CardPaymentBanner ref="cardPaymentBannerRef" />

          <section class="home-discovery">
            <div class="home-discovery__cards">
              <button
                class="home-discovery__card home-discovery__card--rwa"
                type="button"
                @click="openRwaEntry"
              >
                <span
                  class="home-discovery__rwa-glow-mask"
                  :style="{
                    maskImage: `url(${rwaBottomMask})`,
                    WebkitMaskImage: `url(${rwaBottomMask})`,
                  }"
                  aria-hidden="true"
                >
                  <app-image
                    class="home-discovery__rwa-glow"
                    :src="rwaBottomGlow"
                    alt=""
                  />
                </span>
                <app-image
                  class="home-discovery__rwa-art"
                  :src="rwaGlobe"
                  alt=""
                />
                <span class="home-discovery__rwa-copy">
                  <span class="home-discovery__rwa-heading">
                    <span>{{ t("home.rwaChainTitle") }}</span>
                    <app-image :src="rwaArrow" alt="" />
                  </span>
                  <span class="home-discovery__rwa-subtitle">
                    {{ t("home.rwaChainSubtitle") }}
                  </span>
                </span>
              </button>

              <div class="home-discovery__side-cards">
                <button
                  class="home-discovery__card home-discovery__card--off-ramp"
                  type="button"
                  @click="showOffRampNotice = true"
                >
                  <span class="home-discovery__compact-copy">
                    <span>{{ t("home.offRampTitle") }}</span>
                    <span>{{ t("home.offRampSubtitle") }}</span>
                  </span>
                  <app-image
                    class="home-discovery__off-ramp-art"
                    :src="offRampArt"
                    alt=""
                  />
                </button>
                <button
                  class="home-discovery__card home-discovery__card--apply-card"
                  type="button"
                  @click="router.push('/card/index')"
                >
                  <span class="home-discovery__compact-copy">
                    <span>{{ t("home.applyCardShortcutTitle") }}</span>
                    <span>{{ t("home.applyCardShortcutSubtitle") }}</span>
                  </span>
                  <app-image
                    class="home-discovery__apply-card-art"
                    :src="applyCardArt"
                    alt=""
                  />
                </button>
              </div>
            </div>

            <div class="home-discovery__features">
              <button
                v-for="feature in discoveryFeatures"
                :key="feature.key"
                :class="[
                  'home-discovery__feature',
                  `home-discovery__feature--${feature.key}`,
                ]"
                :style="
                  feature.background
                    ? { backgroundImage: `url(${feature.background})` }
                    : undefined
                "
                type="button"
                @click="openDiscoveryFeature(feature.key)"
              >
                <span class="home-discovery__feature-art" aria-hidden="true">
                  <app-image
                    v-if="feature.ornament"
                    class="home-discovery__feature-ornament"
                    :src="feature.ornament"
                    alt=""
                  />
                  <app-image
                    v-if="feature.key === 'games' && feature.ornament"
                    class="home-discovery__feature-ornament home-discovery__feature-ornament--second"
                    :src="feature.ornament"
                    alt=""
                  />
                  <app-image :src="feature.icon" alt="" />
                </span>
                <span class="home-discovery__feature-label">{{
                  feature.label
                }}</span>
                <app-image
                  class="home-discovery__feature-arrow"
                  :src="feature.arrow"
                  alt=""
                />
              </button>
            </div>
          </section>

          <section class="asset-panel">
            <div class="asset-panel__tabs">
              <div
                v-for="tab in tabs"
                :key="tab.key"
                :class="{ active: activeTab === tab.key }"
                class="asset-panel-list"
                @click="handleTabChange(tab.key)"
              >
                {{ tab.label }}
              </div>
              <button
                v-if="activeTab !== 'records'"
                class="asset-panel__add"
                type="button"
                :style="{ '--asset-panel-add-icon': `url(${addIcon})` }"
                @click="handlePanelAdd"
              />
            </div>

            <div v-if="activeTab === 'tokens'" class="asset-list">
              <button
                v-for="coin in coins"
                :key="coinKey(coin)"
                class="coin-row"
                type="button"
                @click="openCoinDetail(coin)"
              >
                <app-image :src="coinIcon(coin)" alt="" />
                <div>
                  <div data-strong>{{ coin.symbol }}</div>
                  <span
                    >${{ formatMoney(coin.usdtPrice || coin.price || 0) }}</span
                  >
                </div>
                <div>
                  <div data-strong>{{ formatTokenAmount(coin.balance) }}</div>
                  <span>${{ coinTotal(coin) }}</span>
                </div>
              </button>
              <EmptyState v-if="coins.length === 0" />
            </div>

            <div v-else-if="activeTab === 'records'" class="asset-list">
              <button
                v-for="record in records"
                :key="record.id || record.hash || record.time"
                class="record-row"
                type="button"
                @click="router.push('/wallet/bill')"
              >
                <div class="record-row__icon">
                  {{ (record.coin || "T").slice(0, 1) }}
                </div>
                <div>
                  <div data-strong>{{ recordTitle(record) }}</div>
                  <span>{{ recordAddress(record) }}</span>
                </div>
                <div>
                  <div data-strong>
                    {{ record.type === "in" ? "+" : "-"
                    }}{{ formatTokenAmount(record.amount) }}
                  </div>
                  <span>{{ record.time || "" }}</span>
                </div>
              </button>
              <EmptyState v-if="records.length === 0" />
            </div>

            <div v-else class="asset-list">
              <van-swipe-cell
                v-for="nft in nfts"
                :key="nft.contractAddress || nft.name"
                class="nft-swipe-cell"
              >
                <button
                  class="nft-row"
                  type="button"
                  @click="openNftDetail(nft)"
                >
                  <app-image :src="nft.img || emptyIcon" alt="" />
                  <div class="nft-row__meta">
                    <div data-strong>{{ nft.name || nft.symbol || "NFT" }}</div>
                    <span>{{ nft.contractAddress || "--" }}</span>
                  </div>
                  <div data-strong>{{ nft.amount || 0 }}</div>
                </button>

                <!-- <template #right>
                  <van-button
                    square
                    type="danger"
                    class="nft-delete-btn"
                    @click="removeNft(nft)"
                  >
                    {{ t('wallet.removeNft') }}
                  </van-button>
                </template> -->
              </van-swipe-cell>
              <EmptyState v-if="nfts.length === 0" />
            </div>
          </section>
        </div>
      </van-pull-refresh>
    </div>

    <section v-if="homeBlockedReason" class="home-mask">
      <div class="home-mask__card">
        <p>{{ homeBlockedReason }}</p>
        <van-button round type="primary" @click="handleBlockedAction">
          {{ homeBlockedAction }}
        </van-button>
      </div>
    </section>

    <WalletMoreSheet
      :model-value="showMoreSheet"
      :restore-instantly="restoreMoreSheetInstantly"
      @update:model-value="setShowMoreSheet"
      @preserve="persistMoreSheetState"
      @select="handleMoreSheetSelect"
    />
    <HomeAccountSheet v-model="showAccountSheet" @selected="loadHomeData" />
    <CardCodeRedeemPopup v-model:show="showCardCodePopup" />

    <van-popup
      v-model:show="showFirstExplore"
      round
      position="bottom"
      class="first-explore-popup"
    >
      <div class="first-explore-popup__body">
        <app-image
          :src="successPopupImg"
          alt=""
          class="first-explore-popup__image"
        />
        <h3>{{ firstExploreTitle }}</h3>
        <p>{{ firstExploreSubtitle }}</p>
        <van-button block round type="primary" @click="handleFirstExplore">
          {{ t("auth.startExplore") }}
        </van-button>
      </div>
    </van-popup>

    <van-popup
      v-model:show="showOffRampNotice"
      position="center"
      class="home-off-ramp-popup"
      :close-on-click-overlay="true"
    >
      <div class="home-off-ramp-dialog" role="dialog" aria-modal="true">
        <h2 class="home-off-ramp-dialog__title">
          {{ t("home.featureNoticeTitle") }}
        </h2>
        <button
          type="button"
          class="home-off-ramp-dialog__close"
          :aria-label="t('common.close')"
          @click="showOffRampNotice = false"
        >
          <app-image :src="offRampCloseIcon" alt="" />
        </button>
        <p class="home-off-ramp-dialog__message">
          <span>{{ t("home.offRampNoticeTitle") }}</span>
          <span>{{ t("home.offRampNoticeDesc") }}</span>
        </p>
        <button
          type="button"
          class="home-off-ramp-dialog__confirm"
          @click="showOffRampNotice = false"
        >
          {{ t("home.offRampNoticeConfirm") }}
        </button>
      </div>
    </van-popup>
  </section>
</template>

<script setup>
import {
  computed,
  defineComponent,
  h,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
} from "vue";
import { closeToast, showFailToast, showLoadingToast, showToast } from "vant";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import {
  getRouteState,
  navigateWithState,
  saveRouteState,
} from "@/utils/route-state";
import { resolveAvatarText, resolveAvatarUrl } from "@/utils/avatar";
import { useProfileStore } from "@/stores/profile";
import { useWebModalStore } from "@/stores/web-modal";
import {
  DEFAULT_BRAND_NAME,
  resolveBrandName,
  useThemeStore,
} from "@/stores/theme";
import { useWalletTokenStore } from "@/stores/wallet-token";
import { useWalletNftStore } from "@/stores/wallet-nft";
import {
  pcCardAssetList,
  pcCardLogLast,
  transactionListV2,
} from "@/api/wallet";
import { cardMyList, pcCardCreatePre } from "@/api/card";
import { getDeviceId } from "@/utils/device";
import {
  formatMoney,
  formatTimelineTime,
  formatTokenAmount,
  shortAddress,
} from "@/utils/format";
import { getRecoverableMnemonic } from "@/utils/walletSession";
import WalletMoreSheet from "@/view/wallet/components/WalletMoreSheet.vue";
import HomeAccountSheet from "@/view/main/components/HomeAccountSheet.vue";
import CardPaymentBanner from "@/view/main/components/CardPaymentBanner.vue";
import CardCodeRedeemPopup from "@/view/main/components/CardCodeRedeemPopup.vue";
import CustomPullRefreshHead from "@/view/main/components/CustomPullRefreshHead.vue";
import eyeOpenIcon from "@/assets/images/eye_open.png";
import languageIcon from "@/assets/images/homeImage/changeLanguage_icon.png";
import eyeCloseIcon from "@/assets/images/eye_close.png";
import withdrawIcon from "@/assets/images/homeImage/withdraw_icon.png";
import billIcon from "@/assets/images/homeImage/wastebook_icon.png";
import transferIcon from "@/assets/images/homeImage/transfer_icon.png";
import moreIcon from "@/assets/images/homeImage/morethan_icon.png";
import emptyIcon from "@/assets/images/homeImage/emptyFlag.png";
import networkFallbackIcon from "@/assets/images/homeImage/network_icon.png";
import notifyIcon from "@/assets/images/notify_icon.png";
import usdtIcon from "@/assets/images/usdt.png";
import bnbIcon from "@/assets/images/icon_bnb.png";
import ethIcon from "@/assets/images/icon_ethereum.png";
import addIcon from "@/assets/images/add_icon.png";
import successPopupIcon from "@/assets/images/SVG/img11.png";
import assetBg from "@/assets/images/SVG/asset-bg.svg";
import rwaGlobe from "@/assets/images/homeImage/discovery/rwa-globe.png";
import offRampArt from "@/assets/images/homeImage/discovery/off-ramp.png";
import applyCardArt from "@/assets/images/homeImage/discovery/apply-card.svg";
import rwaBottomMask from "@/assets/images/homeImage/discovery/rwa-bottom-mask.svg";
import rwaBottomGlow from "@/assets/images/homeImage/discovery/rwa-bottom-glow.svg";
import rwaArrow from "@/assets/images/homeImage/discovery/rwa-arrow.svg";
import webxIcon from "@/assets/images/homeImage/discovery/webx-icon.png";
import webxOrnament from "@/assets/images/homeImage/discovery/webx-ornament.svg";
import webxArrow from "@/assets/images/homeImage/discovery/webx-arrow.svg";
import socialBackground from "@/assets/images/homeImage/discovery/social-bg.svg";
import socialIcon from "@/assets/images/homeImage/discovery/social-icon.png";
import socialOrnament from "@/assets/images/homeImage/discovery/social-ornament.svg";
import socialArrow from "@/assets/images/homeImage/discovery/social-arrow.svg";
import gamesIcon from "@/assets/images/homeImage/discovery/games-icon.png";
import gamesOrnament from "@/assets/images/homeImage/discovery/games-ornament.svg";
import moviesIcon from "@/assets/images/homeImage/discovery/movies-icon.png";
import moviesOrnament from "@/assets/images/homeImage/discovery/movies-ornament.svg";
import offRampCloseIcon from "@/assets/images/homeImage/discovery/off-ramp-close.svg";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const profileStore = useProfileStore();
const webModalStore = useWebModalStore();
const themeStore = useThemeStore();
const walletTokenStore = useWalletTokenStore();
const walletNftStore = useWalletNftStore();
const refreshing = ref(false);
const showBalance = ref(true);
const activeTab = ref("tokens");
const coins = computed(() => walletTokenStore.assetList);
const records = ref([]);
const nfts = computed(() => walletNftStore.nftList);
const cardTotal = ref(0);
const accountTotal = ref(0);
const cardCount = ref(0);
const showMoreSheet = ref(false);
const showAccountSheet = ref(false);
const lastTrade = ref({});
const lastTradeShow = ref(false);
const showFirstExplore = ref(false);
const showCardCodePopup = ref(false);
const showOffRampNotice = ref(false);
const showNotifyBar = ref(false);
const kycInfo = ref({});
const silentLoadingConfig = reactive({ meta: { showLoading: true } });
const homeHeaderRef = ref(null);
const homeHeaderOffset = ref(68);
const homeRouteState = computed(() => getRouteState(route, "home-index", {}));
const restoreMoreSheetInstantly = ref(false);
const cardPaymentBannerRef = ref(null);
const homeFeatureLinks = ref({});

const defaultHomeFeatureLinks = Object.freeze({
  webx: "https://hk.webx.ai/h5",
  social: "https://webx.ai",
  games: "https://g.bingo.vip",
  movies: "https://tv.bingo.vip",
});

const EmptyState = defineComponent({
  setup() {
    return () =>
      h("div", { class: "empty-state" }, [
        h("img", { class: "empty-state-img", src: emptyIcon, alt: "" }),
        h("span", t("home.noData")),
      ]);
  },
});

const successPopupImg = computed(() =>
  themeStore.image("img11", successPopupIcon),
);

const accountName = computed(() =>
  resolveBrandName(
    profileStore.currentAccount.name,
    themeStore.name || DEFAULT_BRAND_NAME,
  ),
);
const avatarText = computed(() => resolveAvatarText(accountName.value));
const avatarUrl = computed(() =>
  resolveAvatarUrl(profileStore.currentAccount.avatar),
);
const networkName = computed(
  () =>
    profileStore.currentNetwork.name ||
    profileStore.currentNetwork.symbol ||
    "Network",
);
const currentNetworkIcon = computed(() => {
  if (profileStore.currentNetwork?.img) {
    return profileStore.currentNetwork.img;
  }
  const symbol = String(
    profileStore.currentNetwork?.symbol ||
      profileStore.currentNetwork?.name ||
      "",
  ).toUpperCase();
  if (symbol.includes("BNB")) return bnbIcon;
  if (symbol.includes("ETH")) return ethIcon;
  return networkFallbackIcon;
});
const nodeStatusClass = computed(() => {
  const status = profileStore.currentNetwork.currentNode?.status || 1;
  if (status <= 1) return "node-chip__dot--ok";
  if (status === 2) return "node-chip__dot--warn";
  return "node-chip__dot--bad";
});
const homeBlockedReason = computed(() => {
  if (profileStore.currentNetwork.supportChat === false)
    return t("article.unsupportedNetwork");
  if (profileStore.currentAccount.type === "1")
    return t("article.observerWalletWarn");
  return "";
});
const homeBlockedAction = computed(() =>
  profileStore.currentNetwork.supportChat === false
    ? t("article.switchNetwork")
    : t("article.switchWallet"),
);
const assetTotal = computed(() => cardTotal.value + accountTotal.value);
const assetTotalText = computed(() => formatMoney(assetTotal.value));
const showBackupButton = computed(() =>
  Boolean(getRecoverableMnemonic(profileStore.accountStorageAddress)),
);
const homeLayoutStyle = computed(() => ({
  "--home-header-offset": `${homeHeaderOffset.value}px`,
}));
const firstExploreTitle = computed(() => {
  const type = profileStore.profile?.setWalletType || "";
  if (type === "createWallet") return t("auth.firstExploreCreateTitle");
  if (type === "createEmail") return t("auth.firstExploreRegisterTitle");
  if (type === "loginEmail") return t("auth.firstExploreLoginTitle");
  if (type === "rememberWords" || type === "privateKey")
    return t("auth.firstExploreImportTitle");
  return t("auth.firstExploreLoginTitle");
});
const firstExploreSubtitle = computed(() => {
  const type = profileStore.profile?.setWalletType || "";
  if (type === "createWallet") return t("auth.firstExploreCreateSubtitle");
  return t("auth.firstExploreDefaultSubtitle");
});

const shortcuts = computed(() => [
  { label: t("home.topup"), svgIcon: "plus", to: "/wallet/topup" },
  { label: t("home.withdraw"), icon: withdrawIcon, to: "/wallet/withdraw" },
  { label: t("home.bill"), icon: billIcon, to: "/wallet/bill" },
  {
    label: t("home.transfer"),
    icon: transferIcon,
    to: "/wallet/transfer",
  },
  { label: t("home.more"), icon: moreIcon, action: "more" },
]);
const discoveryFeatures = computed(() => [
  {
    key: "webx",
    label: t("home.webxMerchant"),
    icon: webxIcon,
    ornament: webxOrnament,
    arrow: webxArrow,
  },
  {
    key: "social",
    label: t("home.socialFeature"),
    icon: socialIcon,
    ornament: socialOrnament,
    arrow: socialArrow,
  },
  {
    key: "games",
    label: t("home.gamesFeature"),
    icon: gamesIcon,
    ornament: gamesOrnament,
    arrow: socialArrow,
  },
  {
    key: "movies",
    label: t("home.moviesFeature"),
    icon: moviesIcon,
    ornament: moviesOrnament,
    arrow: socialArrow,
  },
]);
const tabs = computed(() => [
  { key: "tokens", label: t("home.token") },
  { key: "records", label: t("home.turnover") },
  { key: "nfts", label: t("home.nft") },
]);

function showTodo() {
  showToast(t("home.actionTodo"));
}

function openExternalUrl(value, title = "") {
  const url = String(value || "").trim();
  if (!/^https?:\/\//i.test(url)) return;
  webModalStore.open({ url, title, navBackgroundColor: "#fff" });
}

function openRwaEntry() {
  openExternalUrl("https://h5.rwa.pro", t("home.rwaChainTitle"));
}

function resolveHomeFeatureUrl(key) {
  return homeFeatureLinks.value[key] || defaultHomeFeatureLinks[key] || "";
}

function openDiscoveryFeature(key) {
  const url = resolveHomeFeatureUrl(key);
  if (url) {
    const title =
      discoveryFeatures.value.find((feature) => feature.key === key)?.label ||
      "";
    openExternalUrl(url, title);
    return;
  }
  showTodo();
}

function handlePanelAdd() {
  if (activeTab.value === "tokens") {
    router.push("/wallet/add-token");
    return;
  }
  if (activeTab.value === "nfts") {
    router.push("/wallet/add-nft");
    return;
  }
  showTodo();
}

function handleBlockedAction() {
  if (profileStore.currentNetwork.supportChat === false) {
    router.push("/home/network");
    return;
  }
  showAccountSheet.value = true;
}

function openCoinDetail(coin) {
  walletTokenStore.setCurrentAsset(coin);
  navigateWithState(router, "/wallet/token-detail", {
    namespace: "wallet-token-detail",
    payload: {
      symbol: coin.symbol || coin.coin || "",
      contractAddress: coin.contractAddress || "",
    },
  });
}

function openNftDetail(nft) {
  if (String(profileStore.currentAccount?.type || "") === "1") {
    return;
  }
  walletNftStore.setCurrentNft(nft);
  walletNftStore.setCurrentNftAsset(null);
  router.push("/wallet/nft-detail");
}

function removeNft(nft) {
  walletNftStore.dismissNft(
    nft?.contractAddress,
    profileStore.currentNetwork?.chainNum || 56,
  );
}

async function handleNodeSelected() {
  await loadActiveTabData();
}

function openKycNotice() {
  navigateWithState(router, "/card/kyc/result", {
    namespace: "card-kyc-result",
    payload: {
      holderStatus: String(kycInfo.value?.holderStatus || ""),
      reason: kycInfo.value?.reason || "",
    },
  });
}

function runShortcut(item) {
  if (item.to) {
    router.push(item.to);
    return;
  }
  if (item.action === "more") {
    setShowMoreSheet(true);
    return;
  }
  if (item.action === "open-card") {
    goOpenCard();
    return;
  }
  showTodo();
}

function persistMoreSheetState() {
  const stateKey = saveRouteState("home-index", { showMoreSheet: true });
  window.history.replaceState(
    {
      ...(window.history.state || {}),
      __routeStateKey: stateKey,
      __routeStateNamespace: "home-index",
    },
    "",
  );
}

function setShowMoreSheet(value) {
  showMoreSheet.value = Boolean(value);
  const stateKey = saveRouteState("home-index", {
    showMoreSheet: showMoreSheet.value,
  });
  window.history.replaceState(
    {
      ...(window.history.state || {}),
      __routeStateKey: stateKey,
      __routeStateNamespace: "home-index",
    },
    "",
  );
}

function handleMoreSheetSelect(item) {
  if (item?.action === "open-card") {
    goOpenCard();
  }
}

function coinKey(coin) {
  return coin.contractAddress || coin.coin || coin.symbol || coin.symbolType;
}

function coinIcon(coin) {
  const name = String(coin.coin || coin.symbol || "").toUpperCase();
  if (name.includes("USDT")) return usdtIcon;
  if (name.includes("BNB")) return bnbIcon;
  if (name.includes("ETH")) return ethIcon;
  return coin.img || coin.logo || usdtIcon;
}

function coinTotal(coin) {
  if (
    coin.balanceTo !== undefined &&
    coin.balanceTo !== null &&
    coin.balanceTo !== ""
  ) {
    return formatMoney(Number(coin.balanceTo || 0));
  }
  return formatMoney(
    Number(coin.balance || 0) * Number(coin.usdtPrice || coin.price || 0),
  );
}

function recordDirection(record) {
  const type = String(record?.type || "").toLowerCase();
  if (type === "in" || type === "out") {
    return type;
  }

  const currentAddress = String(
    profileStore.currentAddress || "",
  ).toLowerCase();
  const toAddress = String(record?.toAddress || "").toLowerCase();
  if (currentAddress && toAddress && currentAddress === toAddress) {
    return "in";
  }

  return "out";
}

function recordTitle(record) {
  const direction =
    recordDirection(record) === "in"
      ? t("wallet.transferIn")
      : t("wallet.transferOut");
  return `${direction} ${record.amount || 0} ${record.coin || ""}`;
}

function recordAddress(record) {
  return recordDirection(record) === "in"
    ? shortAddress(record.fromAddress) || "--"
    : shortAddress(record.toAddress) || "--";
}

function formatTradeTime(timestamp) {
  const value = Number(timestamp || 0);
  if (!value) return "";
  return formatTimelineTime(value < 10000000000 ? value * 1000 : value);
}

async function handleRefresh() {
  refreshing.value = true;
  Object.assign(silentLoadingConfig, { meta: { showLoading: false } });
  await Promise.all([loadHomeData(), cardPaymentBannerRef.value?.reload?.()]);
  refreshing.value = false;
}

function handleTabChange(tabKey) {
  if (activeTab.value === tabKey) {
    return;
  }
  activeTab.value = tabKey;
  loadActiveTabData();
}

async function loadHomeData() {
  const address = profileStore.platformAddress || null;
  await Promise.allSettled([
    loadCardAssets(address),
    loadLastTrade(address),
    loadCardCreatePre(address),
  ]);

  if (activeTab.value !== "tokens") {
    await loadActiveTabData();
  }
  Object.assign(silentLoadingConfig, { meta: { showLoading: false } });
}

async function loadActiveTabData() {
  const address = profileStore.platformAddress || null;
  const chainNum = profileStore.currentNetwork.chainNum || 56;

  if (activeTab.value === "tokens") {
    await loadCardAssets(address);
    return;
  }

  if (activeTab.value === "records") {
    await loadRecords(profileStore.currentAddress || null, chainNum);
    return;
  }

  if (activeTab.value === "nfts") {
    await loadNfts(profileStore.platformAddress || null, chainNum);
  }
}

function withTimeout(task, fallback, timeout = 8000) {
  return Promise.race([
    task,
    new Promise((resolve) => {
      setTimeout(() => resolve(fallback), timeout);
    }),
  ]);
}

async function loadRecords(address, chainNum) {
  if (!address) {
    records.value = [];
    return;
  }
  try {
    const result = await withTimeout(
      transactionListV2(
        {
          address,
          chainNum,
          contractAddress: "0x",
          type: null,
          pageNum: 1,
        },
        silentLoadingConfig,
      ),
      [],
    );
    records.value = Array.isArray(result?.list)
      ? result.list
      : Array.isArray(result)
        ? result
        : [];
  } catch (error) {
    records.value = [];
  }
}

async function loadNfts(address, chainNum) {
  if (!address) {
    walletNftStore.setNftList([], chainNum);
    return;
  }
  try {
    await withTimeout(
      walletNftStore.fetchNftList(address, silentLoadingConfig),
      [],
    );
  } catch (error) {
    walletNftStore.setNftList([], chainNum);
  }
}

async function loadCardAssets(address) {
  const chainNum = profileStore.currentNetwork.chainNum || 56;
  if (!address) {
    cardTotal.value = 0;
    accountTotal.value = 0;
    cardCount.value = 0;
    walletTokenStore.setAssetList([], chainNum);
    profileStore.updateAccountSnapshot({
      assetTotal: 0,
      accountAssetTotal: 0,
      cardAssetTotal: 0,
      cardCount: 0,
      mainBalance: 0,
      mainCoin:
        profileStore.currentNetwork?.coin ||
        profileStore.currentNetwork?.symbol ||
        "",
    });
    return;
  }
  try {
    const [assetResult, cardResult] = await Promise.all([
      withTimeout(pcCardAssetList({ address }, silentLoadingConfig), {}),
      withTimeout(
        cardMyList(
          { address, pageNum: 1, pageSize: 999, deviceToken: getDeviceId() },
          silentLoadingConfig,
        ),
        {},
      ),
    ]);
    const list = Array.isArray(cardResult?.list)
      ? cardResult.list
      : Array.isArray(cardResult)
        ? cardResult
        : [];
    cardCount.value = list.length;
    cardTotal.value = list.reduce(
      (sum, item) => sum + Number(item?.balance?.available || 0),
      0,
    );
    accountTotal.value = Number(assetResult?.totalTo || 0);
    const assetList = Array.isArray(assetResult?.list) ? assetResult.list : [];
    walletTokenStore.setAssetList(assetList, chainNum);
    const mainCoin = String(
      profileStore.currentNetwork?.coin ||
        profileStore.currentNetwork?.symbol ||
        "",
    )
      .trim()
      .toUpperCase();
    const mainAsset =
      assetList.find(
        (item) =>
          String(item?.symbol || item?.coin || "")
            .trim()
            .toUpperCase() === mainCoin,
      ) || null;
    profileStore.updateAccountSnapshot({
      assetTotal: cardTotal.value + accountTotal.value,
      accountAssetTotal: accountTotal.value,
      cardAssetTotal: cardTotal.value,
      cardCount: cardCount.value,
      mainBalance: mainAsset?.balanceNormal ?? mainAsset?.balance ?? 0,
      mainCoin:
        mainCoin ||
        profileStore.currentNetwork?.coin ||
        profileStore.currentNetwork?.symbol ||
        "",
    });
  } catch (error) {
    cardTotal.value = 0;
    accountTotal.value = 0;
    cardCount.value = 0;
    walletTokenStore.setAssetList(
      [],
      profileStore.currentNetwork.chainNum || 56,
    );
    profileStore.updateAccountSnapshot({
      assetTotal: 0,
      accountAssetTotal: 0,
      cardAssetTotal: 0,
      cardCount: 0,
      mainBalance: 0,
      mainCoin:
        profileStore.currentNetwork?.coin ||
        profileStore.currentNetwork?.symbol ||
        "",
    });
  }
}

async function loadLastTrade(address) {
  if (!address) {
    lastTrade.value = {};
    lastTradeShow.value = false;
    return;
  }
  try {
    const result = await withTimeout(
      pcCardLogLast({ address }, silentLoadingConfig),
      {},
    );
    lastTrade.value = result && typeof result === "object" ? result : {};
    lastTradeShow.value = !!Object.keys(lastTrade.value).length;
  } catch (error) {
    lastTrade.value = {};
    lastTradeShow.value = false;
  }
}

async function loadCardCreatePre(address) {
  if (!address) {
    kycInfo.value = {};
    showNotifyBar.value = false;
    return;
  }

  try {
    const result = await pcCardCreatePre({ address }, silentLoadingConfig);
    kycInfo.value = result && typeof result === "object" ? result : {};
    showNotifyBar.value = !!String(kycInfo.value?.reason || "").trim();
  } catch (error) {
    kycInfo.value = {};
    showNotifyBar.value = false;
  }
}

async function goOpenCard() {
  if (!profileStore.platformAddress) {
    router.replace("/login");
    return;
  }

  showLoadingToast({
    message: t("common.loading"),
    forbidClick: true,
    duration: 0,
  });
  try {
    const result = await pcCardCreatePre({
      address: profileStore.platformAddress,
    });
    const holderStatus = Number(result?.holderStatus || 0);
    if (holderStatus === 3) {
      navigateWithState(router, "/wallet/open-card", {
        namespace: "card-open",
        payload: { type: 2 },
      });
      return;
    }
    if (
      holderStatus === 2 ||
      holderStatus === 4 ||
      (holderStatus === 1 && String(result?.reason || "").trim())
    ) {
      navigateWithState(router, "/card/kyc/result", {
        namespace: "card-kyc-result",
        payload: {
          holderStatus: String(holderStatus),
          reason: result?.reason || "",
        },
      });
      return;
    }
    router.push("/card/kyc");
  } catch (error) {
    showFailToast(error.message || t("common.retryLater"));
  } finally {
    closeToast();
  }
}

function openFirstExplorePopup() {
  if (!profileStore.profile?.isFirst) {
    return;
  }
  showFirstExplore.value = true;
  profileStore.setFirstVisit(false);
}

function handleFirstExplore() {
  showFirstExplore.value = false;
  if (!homeRouteState.value?.openCardCodePopupAfterExplore) {
    return;
  }

  showCardCodePopup.value = true;
  navigateWithState(router, route.path, {
    namespace: "home-index",
    replace: true,
    payload: {
      ...homeRouteState.value,
      openCardCodePopupAfterExplore: false,
    },
  });
}

function updateHomeHeaderOffset() {
  const headerHeight = Math.ceil(
    homeHeaderRef.value?.getBoundingClientRect?.().height || 0,
  );
  if (headerHeight > 0) {
    homeHeaderOffset.value = headerHeight + 8;
  }
}

function syncHomeViewportLayout() {
  updateHomeHeaderOffset();
}

function resetHomeScroll() {
  const activeEl = document.activeElement;
  if (
    activeEl &&
    typeof activeEl.blur === "function" &&
    (activeEl.tagName === "INPUT" ||
      activeEl.tagName === "TEXTAREA" ||
      activeEl.tagName === "SELECT" ||
      activeEl.isContentEditable)
  ) {
    activeEl.blur();
  }

  window.scrollTo(0, 0);
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  const refreshEl = document.querySelector(".home-refresh");
  const scrollTarget =
    refreshEl?.querySelector?.(".van-pull-refresh") || refreshEl;
  scrollTarget?.scrollTo?.({ top: 0, behavior: "auto" });
}

onMounted(async () => {
  const shouldRestoreMoreSheet = Boolean(homeRouteState.value?.showMoreSheet);
  restoreMoreSheetInstantly.value = shouldRestoreMoreSheet;
  showMoreSheet.value = shouldRestoreMoreSheet;
  await loadHomeData();
  await nextTick();
  if (shouldRestoreMoreSheet) {
    requestAnimationFrame(() => {
      restoreMoreSheetInstantly.value = false;
    });
  }
  syncHomeViewportLayout();
  requestAnimationFrame(() => {
    syncHomeViewportLayout();
    resetHomeScroll();
  });
  window.setTimeout(() => {
    syncHomeViewportLayout();
    resetHomeScroll();
  }, 120);
  window.setTimeout(() => {
    resetHomeScroll();
  }, 260);
  window.addEventListener("resize", syncHomeViewportLayout, { passive: true });
  window.visualViewport?.addEventListener?.("resize", syncHomeViewportLayout, {
    passive: true,
  });
  window.visualViewport?.addEventListener?.("scroll", syncHomeViewportLayout, {
    passive: true,
  });
  openFirstExplorePopup();
});

onUnmounted(() => {
  window.removeEventListener("resize", syncHomeViewportLayout);
  window.visualViewport?.removeEventListener?.(
    "resize",
    syncHomeViewportLayout,
  );
  window.visualViewport?.removeEventListener?.(
    "scroll",
    syncHomeViewportLayout,
  );
});
</script>

<style scoped lang="scss">
.home-tab {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100%;
  min-width: 0;
  padding: 0 14px 0;
  overflow: hidden;
  background: #ffffff;
}

.home-body {
  flex: 1;
  min-height: 0;
  min-width: 0;
  margin-top: var(
    --home-header-offset,
    calc(60px + env(safe-area-inset-top) + 8px)
  );
  overflow: hidden;
}

.home-refresh {
  height: 100%;
  min-width: 0;
  overflow: auto;
}

.home-content {
  min-width: 0;
  padding-top: 2px;
  padding-bottom: calc(68px + env(safe-area-inset-bottom) + 16px);
}

.first-explore-popup {
  padding: 16px 16px calc(20px + env(safe-area-inset-bottom));
}

.first-explore-popup__body {
  text-align: center;

  h3 {
    margin: 8px 0 10px;
    color: #000000;
    font-size: 20px;
    font-weight: 700;
  }

  p {
    margin: 0 0 24px;
    color: #666666;
    font-size: 14px;
    line-height: 22px;
  }
}

.first-explore-popup__image {
  display: block;
  width: 125px;
  margin: 0 auto;
  object-fit: contain;
}

.trade-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding: 12px 14px;
  border-radius: 8px;
  background: #eef5ff;

  button {
    flex: 0 0 auto;
    border: 0;
    background: transparent;
    color: var(--wp-color-sub-text);
    font-size: 12px;
  }
}

.fw {
  font-weight: bold;
}

.notify-banner {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  margin-top: 2px;
  margin-bottom: 10px;
  padding: 5px 8px 5px 10px;
  border-radius: 8px;
  background: #eef5ff;
}

.notify-banner__content {
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  gap: 2px;
  color: var(--wp-color-brand-1);
  font-size: 13px;
  line-height: 18px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.notify-banner__icon {
  width: 20px;
  height: 20px;
  flex: 0 0 auto;
  object-fit: contain;
}

.notify-banner__action,
.notify-banner__close {
  border: 0;
  background: transparent;
}

.notify-banner__action {
  flex: 0 0 auto;
  height: 24px;
  padding: 0 10px;
  border: 1px solid var(--wp-color-brand-1);
  border-radius: 999px;
  color: var(--wp-color-brand-1);
  font-size: 12px;
  line-height: 22px;
}

.notify-banner__close {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  color: #999999;
  font-size: 14px;
}

.trade-banner__content {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  gap: 4px 8px;
  color: var(--wp-color-theme);
  font-size: 12px;

  [data-strong] {
    color: var(--wp-color-text);
    font-size: 13px;
  }

  .fail {
    color: #f53f3f;
  }
}

.home-header {
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: calc(60px + env(safe-area-inset-top));
  padding-top: env(safe-area-inset-top);
  box-sizing: border-box;
  background: #fff;
  position: fixed;
  left: 14px;
  top: 0;
  right: 14px;
}

.account-chip,
.node-chip,
.language-chip,
.network-chip {
  border: 0;
  background: transparent;
}

.account-chip {
  display: flex;
  min-width: 0;
  align-items: center;
  padding: 0;
  color: var(--wp-color-text);
}

.account-chip__avatar {
  display: flex;
  flex: 0 0 32px;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    var(--wp-color-brand-2),
    var(--wp-color-brand-3)
  );
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
}

.account-chip__avatar-image {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.account-chip__meta {
  min-width: 0;
  margin-left: 7px;
  text-align: left;

  .name,
  span {
    display: block;
    overflow: hidden;
    max-width: 118px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .name {
    font-size: 13px;
    font-weight: 500;

    i {
      display: inline-block;
      margin-left: 2px;
    }
  }

  span {
    margin-top: 2px;
    color: var(--wp-color-sub-text);
    font-size: 12px;
  }

  .current-card-bar__caret {
    width: 0;
    height: 0;
    border-top: 6px solid #000;
    border-right: 5px solid transparent;
    border-left: 5px solid transparent;
  }
}

.home-header__tools {
  display: flex;
  align-items: center;
  gap: 5px;
}

.node-chip {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid #c9cdd4;
  border-radius: 10px;
}

.node-chip__dot {
  width: 10px;
  height: 10px;
  border: 2px solid rgba(0, 180, 42, 0.3);
  border-radius: 50%;
  background: #00b42a;
}

.node-chip__dot--warn {
  border-color: rgba(250, 140, 21, 0.3);
  background: #fa8c15;
}

.node-chip__dot--bad {
  border-color: rgba(245, 63, 63, 0.3);
  background: #f53f3f;
}

.language-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;

  .app-image {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }
}

.network-chip {
  display: flex;
  align-items: center;
  max-width: 148px;
  height: 34px;
  padding: 5px 8px;
  border: 1px solid #e5e5e5;
  border-radius: 50px;
  color: var(--wp-color-text);
  font-size: 12px;

  .app-image {
    width: 22px;
    height: 22px;
  }

  span {
    overflow: hidden;
    max-width: 78px;
    margin: 0 5px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .app-image:last-child {
    width: 16px;
    height: 16px;
    margin-left: 5px;
  }
}

.asset-card {
  --asset-card-background: var(--wp-color-brand-1);
  position: relative;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 23px 16px;
  border-radius: 16px;
  overflow: hidden;
  background-color: var(--wp-color-brand-1);
  color: #ffffff;
  box-shadow: 0 8px 18px rgba(22, 93, 255, 0.18);

  > :not(.asset-card__background) {
    position: relative;
    z-index: 1;
  }

  &__background {
    position: absolute;
    inset: 0;
    z-index: 0;
    width: 100%;
    height: 100%;
    display: block;
    pointer-events: none;
    vertical-align: initial;
  }

  [data-strong] {
    color: #fff;
  }
}

.home-off-ramp-popup {
  width: 300px;
  height: 198px;
  overflow: hidden;
  border: 1px solid #fff;
  border-radius: 16px;
  background: #fff;
}

.home-off-ramp-dialog {
  position: relative;
  width: 100%;
  height: 100%;
  color: #000;
  font-family: inherit;
}

.home-off-ramp-dialog__title {
  position: absolute;
  top: 20px;
  right: 0;
  left: 0;
  margin: 0;
  color: #000;
  font-size: 20px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
}

.home-off-ramp-dialog__close {
  position: absolute;
  top: 18px;
  right: 16px;
  display: flex;
  width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;

  .app-image {
    display: block;
    width: 16.3px;
    height: 16.04px;
  }
}

.home-off-ramp-dialog__message {
  position: absolute;
  top: 62px;
  right: 12px;
  left: 12px;
  display: flex;
  flex-direction: column;
  margin: 0;
  color: #333;
  font-size: 16px;
  font-weight: 500;
  line-height: 25px;
  text-align: center;
}

.home-off-ramp-dialog__confirm {
  position: absolute;
  right: 18px;
  bottom: 26px;
  left: 18px;
  height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 100px;
  background: #7442e8;
  color: #fff;
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;
  line-height: 40px;
  text-align: center;
}

.home-mask {
  position: absolute;
  inset: env(safe-area-inset-top) 0 84px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.7);
}

.home-mask__card {
  width: 100%;
  max-width: 320px;
  text-align: center;

  p {
    margin: 0 0 20px;
    color: var(--wp-color-text);
    font-size: 14px;
    line-height: 22px;
  }
}

.asset-card__title {
  display: flex;
  align-items: center;
  // justify-content: space-between;
  font-size: 16px;

  span {
    font-weight: bold;
  }

  .app-image {
    width: 18px;
    height: 18px;
    filter: brightness(0) invert(1);
  }
}

.asset-card__amount {
  display: flex;
  min-width: 0;
  align-items: flex-end;
  margin-top: 13px;

  [data-strong] {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 24px;
    font-weight: 700;
  }

  span {
    flex: 0 0 auto;
    margin-left: 8px;
    font-size: 15px;
    font-weight: 500;
    margin-bottom: 3px;
  }

  button {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 30px;
    margin-left: auto;
    padding: 0 10px;
    border: 1px solid #f2f3f5;
    border-radius: 50px;
    background: #f7f8fa;
    color: #000000;
    font-size: 14px;
  }
}

.asset-card__stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  min-width: 0;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.15);

  button {
    min-width: 0;
    min-width: 0;
    padding: 0 12px;
    border: 0;
    border-right: 1px solid rgba(255, 255, 255, 0.15);
    background: transparent;
    color: #ffffff;
    text-align: left;

    &:first-child {
      padding-left: 0;
    }

    &:last-child {
      border-right: 0;
      padding-right: 0;
    }
  }

  span,
  [data-strong] {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    font-size: 12px;
    font-weight: bold;
  }

  [data-strong] {
    margin-top: 5px;
    font-size: 13px;
    font-weight: 700;
  }
}

.shortcut-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin-top: 20px;
  text-align: center;

  &-list {
    border: 0;
    display: flex;
    flex-direction: column;
    background: transparent;
    color: var(--wp-color-text);
    font-size: 12px;
  }

  .app-image,
  .app-svg-icon {
    width: 48px;
    height: 48px;
    margin: 0 auto 10px;
  }
}

.home-discovery {
  width: calc(100% - 4px);
  margin: 18px 2px 0;
}

.home-discovery__cards {
  display: grid;
  grid-template-columns: minmax(0, 164fr) minmax(0, 167fr);
  gap: 12px;
  height: 178px;
}

.home-discovery__card {
  position: relative;
  display: block;
  min-width: 0;
  overflow: hidden;
  padding: 0;
  border: 0;
  background: #f7f3ff;
  color: #000;
  font-family: inherit;
  text-align: left;
  -webkit-tap-highlight-color: transparent;
}

.home-discovery__card--rwa {
  height: 178px;
  border-radius: 12px;
}

.home-discovery__rwa-glow-mask {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;
  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;
}

.home-discovery__rwa-glow {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 31px;
}

.home-discovery__rwa-art {
  position: absolute;
  z-index: 1;
  bottom: 4px;
  left: 22px;
  width: 120px;
  height: 108px;
  object-fit: fill;
  pointer-events: none;
}

.home-discovery__rwa-copy {
  position: absolute;
  z-index: 2;
  top: 16px;
  right: 10px;
  left: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.home-discovery__rwa-heading {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 2px;
  font-size: 16px;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;

  > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .app-image {
    flex: 0 0 16px;
    width: 16px;
    height: 16px;
  }
}

.home-discovery__rwa-subtitle {
  color: #777;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  white-space: nowrap;
}

.home-discovery__side-cards {
  display: grid;
  min-width: 0;
  grid-template-rows: 82px 83px;
  gap: 12px;
}

.home-discovery__card--off-ramp {
  height: 82px;
  border-radius: 16px;
}

.home-discovery__card--apply-card {
  height: 83px;
  border-radius: 12px;
}

.home-discovery__compact-copy {
  position: absolute;
  top: 13px;
  right: 8px;
  left: 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  white-space: nowrap;

  > span:first-child {
    color: #000;
    font-size: 14px;
    font-weight: 600;
    line-height: 16px;
  }

  > span:last-child {
    color: #777;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
  }
}

.home-discovery__card--off-ramp .home-discovery__compact-copy {
  top: 13px;
  right: 52px;
}

.home-discovery__off-ramp-art {
  position: absolute;
  top: 35px;
  right: 5px;
  width: 63px;
  height: 42px;
  pointer-events: none;
}

.home-discovery__apply-card-art {
  position: absolute;
  right: 13px;
  bottom: 10px;
  width: 42px;
  height: 34px;
  pointer-events: none;
}

.home-discovery__features {
  display: flex;
  gap: 10px;
  margin-top: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
}

.home-discovery__feature {
  position: relative;
  display: flex;
  flex: 0 0 auto;
  height: 50px;
  align-items: center;
  gap: 6px;
  overflow: hidden;
  padding: 0 10px;
  border: 0;
  border-radius: 12px;
  background-color: #f7f3ff;
  background-position: center;
  background-repeat: no-repeat;
  background-size: 100% 100%;
  color: #333;
  font-family: inherit;
  text-align: left;
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;

  &--webx {
    width: 145px;
  }

  &--social {
    width: 107px;
  }

  &--games {
    width: 111px;
  }

  &--movies {
    width: 110px;
  }
}

.home-discovery__feature-art {
  position: relative;
  display: block;
  flex: 0 0 auto;
  width: 30px;
  height: 34px;

  > .app-image:last-child {
    position: absolute;
    z-index: 1;
    inset: 0;
    width: 100%;
    height: 100%;
  }
}

.home-discovery__feature--webx .home-discovery__feature-art {
  width: 28px;
  height: 37px;
}

.home-discovery__feature--games .home-discovery__feature-art {
  width: 35px;
  height: 30px;
}

.home-discovery__feature--movies .home-discovery__feature-art {
  width: 32px;
  height: 33px;
}

.home-discovery__feature-ornament {
  position: absolute;
  z-index: 1;
  right: 0;
  bottom: 0;
  width: 26px;
  height: 8px;
}

.home-discovery__feature--webx .home-discovery__feature-ornament {
  left: 0;
  bottom: 0;
  width: 22px;
  height: 11px;
}

.home-discovery__feature--social .home-discovery__feature-ornament {
  left: 0;
  bottom: 0;
  width: 27px;
  height: 6px;
}

.home-discovery__feature--games .home-discovery__feature-ornament {
  left: 0;
  bottom: 2px;
  width: 13px;
  height: 3px;
}

.home-discovery__feature--games .home-discovery__feature-ornament--second {
  right: 0;
  left: auto;
  bottom: 0;
}

.home-discovery__feature--movies .home-discovery__feature-ornament {
  left: 0;
  bottom: 1px;
  width: 26px;
  height: 6px;
}

.home-discovery__feature-label {
  position: relative;
  z-index: 2;
  color: #333;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
}

.home-discovery__feature--webx .home-discovery__feature-label {
  width: 70px;
}

.home-discovery__feature-arrow {
  position: relative;
  z-index: 2;
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
}

.home-divider {
  width: 100%;
  height: 1px;
  margin: 20px 0;
  background: #f4f5f6;
}

.asset-panel {
  margin-top: 20px;
  padding-bottom: 10px;
}

.asset-panel__tabs {
  display: flex;
  align-items: center;

  .asset-panel-list {
    margin-right: 10px;
    position: relative;
    padding: 0 0 10px;
    border: 0;
    background: transparent;
    color: var(--wp-color-sub-text);
    font-size: 18px;
  }

  .active {
    color: #000;
    font-weight: 700;
  }

  .active::after {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    width: 30px;
    height: 3px;
    margin: auto;
    border-radius: 30px;
    background: var(--wp-color-brand-1);
    box-shadow: 0 2px 4px var(--wp-color-brand-6);
    content: "";
  }

  .asset-panel__add {
    margin-left: auto;
    width: 25px;
    height: 25px;
    padding: 0;
    border: 0;
    background-color: #000000;
    mask: var(--asset-panel-add-icon) center / contain no-repeat;
    -webkit-mask: var(--asset-panel-add-icon) center / contain no-repeat;
  }
}

.asset-list {
  margin-top: 10px;
  background: #ffffff;

  .empty-state {
    .app-image {
      width: 200px;
    }
  }
}

.nft-swipe-cell {
  background: #ffffff;
  overflow: hidden;
}

.nft-swipe-cell :deep(.van-swipe-cell__wrapper) {
  background: #ffffff;
}

.nft-swipe-cell :deep(.van-swipe-cell__right) {
  display: flex;
  background: #ee0a24;
}

.coin-row,
.record-row,
.nft-row {
  display: flex;
  width: 100%;
  align-items: center;
  padding: 20px 0;
  border: 0;
  border-bottom: 1px solid #f4f5f6;
  background: #ffffff;
  color: var(--wp-color-text);
  text-align: left;

  > .app-image,
  .record-row__icon {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    margin-right: 10px;
    border-radius: 50%;
  }

  > div:nth-child(2) {
    min-width: 0;
    flex: 1;
  }

  > div:last-child {
    text-align: right;
  }

  [data-strong],
  span {
    display: block;
  }

  [data-strong] {
    font-size: 16px;
    font-weight: 700;
  }

  span {
    margin-top: 4px;
    color: var(--wp-color-sub-text);
    font-size: 13px;
  }
}

.record-row__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--wp-color-theme);
  color: #ffffff;
  font-size: 20px;
  font-weight: 700;
}

.nft-row {
  > .app-image {
    border-radius: 8px;
    object-fit: cover;
  }

  > [data-strong] {
    margin-left: 8px;
    flex-shrink: 0;
  }
}

.nft-row__meta {
  min-width: 0;
  flex: 1;
}

.nft-row__meta [data-strong],
.nft-row__meta span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nft-delete-btn {
  width: 72px;
  min-height: 81px;
  border: 0;
  flex-shrink: 0;
}

.nft-delete-btn:deep(.van-button__text) {
  font-size: 14px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
  color: var(--wp-color-sub-text);
  font-size: 12px;

  &-img {
    width: 200px;
  }
}

:deep(.empty-state-img) {
  width: 200px;
}
</style>
